import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  OpportunityCardDetailService,
  type CrmProvider,
  type CrmRecordSearchItem,
  type CrmRecordMapping,
} from './../../service/opportunity-card-detail.service';
import {
  OpportunityDate,
  OpportunityDetail,
  OpportunityQuestion,
} from '@core/interface/opportunity-card-detail';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { forkJoin, take, of, Subject, Subscription, from } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { AmplitudeService, UserConfigService, NotificationService } from '@core/services';
import { EmailModalComponent } from '@core/component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faClone } from '@fortawesome/free-solid-svg-icons';

interface NextStepGroup {
  heading: string | null;
  items: string[]; // may contain <strong> from inline **bold**
}

@Component({
  selector: 'app-opportunity-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, DatePipe, EmailModalComponent, FontAwesomeModule],
  templateUrl: './opportunity-detail.component.html',
  styleUrl: './opportunity-detail.component.scss',
})
export class OpportunityDetailComponent implements OnInit, AfterViewInit, OnDestroy {

  opportunityCardDetails: OpportunityQuestion[] = [];
  opportunityDates: OpportunityDate[] = [];
  lastUpdated = '';
  isGlobalEditMode = false;
  editValues: Record<string, string> = {};
  isSummaryCollapsed = false;
  isTranscriptCollapsed = false;
  isVideoPlaying = false;
  isVideoLoading = false;
  isUnansweredCollapsed = true;
  isTranscriptCopied = false;
  isDateDropdownOpen = false;
  isLoading = true;

  summaryMode: 'short' | 'extended' = 'short';
  summaryVariants: Record<'short' | 'extended', string> = {
    short: '',
    extended: ''
  };
  isExtendedSummaryAvailable = false;
  extendedSummaryUnavailableCopy = 'Extended summary not available yet.';
  meetingTranscripts: any[] = [];
  nextStepGroups: NextStepGroup[] = [];
  title = '';
  recordingUrl = '';
  emails = [];
  connectedCrmProvider: CrmProvider | null = null;
  crmSearchTerm = '';
  crmSearchResults: CrmRecordSearchItem[] = [];
  isCrmSearchLoading = false;
  isCrmMapping = false;
  isCrmPushing = false;
  selectedCrmRecord: CrmRecordSearchItem | null = null;
  currentCrmMapping: CrmRecordMapping | null = null;
  crmSearchStatusMessage = '';

  @ViewChild('videoPlayer') videoPlayer!: ElementRef<HTMLVideoElement>;
  @ViewChild('leftCol') leftCol!: ElementRef;
  @ViewChild('rightPane') rightPane!: ElementRef;
  @ViewChild('rightScroll') rightScroll!: ElementRef;
  @ViewChild('emailModal') emailModal!: EmailModalComponent;

  private opportunityId = '';
  private _leftColResizeObserver?: ResizeObserver;
  private _crmSearchInput$ = new Subject<string>();
  private _crmSearchSubscription?: Subscription;
  botId = '';
  readonly copyIcon = faClone;
  readonly checkIcon = faCheck;

  constructor(
    private readonly _opportunityCardDetailService: OpportunityCardDetailService,
    private readonly _route: ActivatedRoute,
    private readonly _amplitudeService: AmplitudeService,
    private readonly _userConfigService: UserConfigService,
    private readonly _notificationService: NotificationService
  ) { }

  ngOnInit(): void {
    this.initializeCrmSearchStream();
    this._route.params.subscribe((params) => {
      this.opportunityId = params['opportunityId'];
      this.botId = params['botId'] ?? '';
      this.loadOpportunityDates(this.opportunityId);
      this.loadCrmMappingContext();
    });
    this.title = localStorage.getItem('OPPORTUNITY_NAME') ?? '';
  }

  ngAfterViewInit(): void {
    this.setScrollHeight();
    this.observeLeftColumnHeight();
  }

  ngOnDestroy(): void {
    this._leftColResizeObserver?.disconnect();
    this._crmSearchSubscription?.unsubscribe();
  }

  get canSearchCrmRecords(): boolean {
    return !!this.connectedCrmProvider;
  }

  get hasCrmMapping(): boolean {
    return !!this.currentCrmMapping?.crm_record_id;
  }

  get canPushMeetingSummaryToCrm(): boolean {
    return !!this.connectedCrmProvider && !!this.opportunityId && !!this.botId && this.hasCrmMapping;
  }

  onCrmSearchTermChanged(): void {
    this._crmSearchInput$.next(this.crmSearchTerm);
  }

  async selectCrmRecord(record: CrmRecordSearchItem): Promise<void> {
    if (!this.connectedCrmProvider || this.isCrmMapping || !this.opportunityId) {
      return;
    }

    this.isCrmMapping = true;
    try {
      const response = await this._opportunityCardDetailService.mapCrmRecord(
        this.connectedCrmProvider,
        this.opportunityId,
        record.id,
        record.name,
        record.type
      );

      this.selectedCrmRecord = record;
      console.log(record);

      this.currentCrmMapping = {
        crm_record_id: record.id,
        crm_object_type: record.type,
        provider: this.connectedCrmProvider,
        crm_object_name: record.name
      };


      this.crmSearchTerm = record.name;
      this.crmSearchResults = [];
      this.crmSearchStatusMessage = '';
      this._notificationService.notify(response?.message || 'CRM record mapped successfully');
    } catch (error) {
      console.error('Failed to map CRM record:', error);
      this._notificationService.notify('Failed to map CRM record');
    } finally {
      this.isCrmMapping = false;
    }
  }

  async pushMeetingSummaryToCrm(): Promise<void> {
    if (!this.connectedCrmProvider || !this.canPushMeetingSummaryToCrm || this.isCrmPushing) {
      return;
    }

    this.isCrmPushing = true;
    try {
      const response = await this._opportunityCardDetailService.pushMeetingSummaryToCrm(
        this.connectedCrmProvider,
        this.opportunityId,
        this.botId
      );

      this._notificationService.notify(response?.message || 'Meeting summary pushed to CRM');
      this._amplitudeService.trackEvent('crm_meeting_summary_pushed', {
        opportunity_id: this.opportunityId,
        meeting_bot_id: this.botId,
        provider: this.connectedCrmProvider,
      });
    } catch (error) {
      console.error('Failed to push meeting summary to CRM:', error);
      this._notificationService.notify('Failed to push meeting summary to CRM');
    } finally {
      this.isCrmPushing = false;
    }
  }

  toggleEdit(question_id: string): void {
    this.opportunityCardDetails = this.opportunityCardDetails.map(q => ({
      ...q,
      isEditMode: q.question_id === question_id ? !q.isEditMode : false
    }));
    const target = this.opportunityCardDetails.find(q => q.question_id === question_id);
    if (target) this.editValues[question_id] = target.previous_answer_summary;
  }

  cancel(question_id: string): void {
    this.opportunityCardDetails = this.opportunityCardDetails.map(q =>
      q.question_id === question_id ? { ...q, isEditMode: false } : q
    );
  }

  updateAnswer(question_id: string): void {
    this._amplitudeService.trackEvent('opportunity_detail_edit_answers', {
      opportunity_id: this.opportunityId
    });

    this.opportunityCardDetails = this.opportunityCardDetails.map(q =>
      q.question_id === question_id
        ? { ...q, previous_answer_summary: this.editValues[question_id], isEditMode: false }
        : q
    );
  }

  toggleGlobalEdit(): void {
    this.isGlobalEditMode = !this.isGlobalEditMode;
    this.editValues = this.isGlobalEditMode
      ? Object.fromEntries(
        this.opportunityCardDetails
          .filter(q => q.answers?.[0])
          .map(q => [q.question_id, q.answers![0].answer_text])
      )
      : {};
    this.setScrollHeight();
  }

  updateSingleAnswer(questionId: string): void {
    const input = this.editValues[questionId]?.trim();
    if (!input) return;

    const question = this.opportunityCardDetails.find(q => q.question_id === questionId);
    if (question) {
      question.answers = [{ answer_text: input }];
      delete this.editValues[questionId];
      if (!this.hasUnsavedChanges()) this.isGlobalEditMode = false;
    }
  }

  saveAllAnswers(): void {
    this.opportunityCardDetails.forEach(q => {
      const value = this.editValues[q.question_id]?.trim();
      if (value) {
        q.answers = [{ answer_text: value }];
      }
    });

    const answeredData = this.opportunityCardDetails.filter(
      q => q.answers && q.answers.length > 0
    );

    const requests = answeredData.map(q =>
      this._opportunityCardDetailService.updateAnswer(
        q.question_id,
        this.botId,
        q.answers[0].answer_text
      )
    );

    if (requests.length > 0) {
      forkJoin(requests).subscribe({
        next: (results) => {
          console.log('All API calls completed:', results);
        },
        error: (err) => {
          console.error('One or more API calls failed:', err);
        },
      });
    }

    this.editValues = {};
    this.isGlobalEditMode = false;
    this.setScrollHeight();
  }



  cancelEdit(): void {
    this.editValues = {};
    this.isGlobalEditMode = false;
    this.setScrollHeight();
  }

  switchVersion(meeting_bot_id: string): void {
    const selected = this.opportunityDates.find(d => d.meeting_bot_id === meeting_bot_id);
    const createdAt = selected?.created_at ?? '';
    const selectedIndex = this.opportunityDates.findIndex(d => d.meeting_bot_id === meeting_bot_id);

    this._amplitudeService.trackEvent('opportunity_detail_view_previous_updates', {
      opportunity_id: this.opportunityId,
      date_selected: createdAt,
      position: selectedIndex >= 0 ? selectedIndex.toString() : '0'
    });

    this.botId = meeting_bot_id;
    this.lastUpdated = createdAt;
    this.isDateDropdownOpen = false;
    this.loadAllMeetingData(meeting_bot_id, createdAt);
  }

  toggleDateDropdown(): void {
    if (!this.opportunityDates.length) {
      return;
    }

    this.isDateDropdownOpen = !this.isDateDropdownOpen;
  }

  toggleSummary(): void {
    this.isSummaryCollapsed = !this.isSummaryCollapsed;
    this.setScrollHeight();
  }

  toggleTranscript(): void {
    this.isTranscriptCollapsed = !this.isTranscriptCollapsed;
    if (this.isTranscriptCollapsed && this.rightPane?.nativeElement) {
      this.rightPane.nativeElement.style.height = 'auto';
    } else {
      this.setScrollHeight();
    }
  }

  copyTranscript(): void {
    const header = 'convverse.ai - Meeting Transcript\n';
    const separator = '='.repeat(50) + '\n\n';
    const details = `Opportunity: ${this.title}\nTimestamp: ${new Date(this.lastUpdated).toLocaleString()}\n\n`;

    const transcriptContent = this.meetingTranscripts
      .map(item => `${item.speaker}: ${item.transcript}`)
      .join('\n\n');

    const fullText = header + separator + details + transcriptContent;

    navigator.clipboard.writeText(fullText).then(() => {
      this.isTranscriptCopied = true;
      this._amplitudeService.trackEvent('transcript_copied', {
        opportunity_id: this.opportunityId
      });
      setTimeout(() => {
        this.isTranscriptCopied = false;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy transcript:', err);
    });
  }

  downloadRecording(): void {
    if (!this.recordingUrl) return;

    const safeTitle = (this.title || 'meeting')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    const ts = this.lastUpdated
      ? new Date(this.lastUpdated).toISOString().replace(/[:.]/g, '-').slice(0, 19)
      : new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);

    const filename = `${safeTitle || 'meeting'}-${ts}.mp4`;
    const anchor = document.createElement('a');
    anchor.href = this.recordingUrl;
    anchor.download = filename;
    anchor.rel = 'noopener noreferrer';
    anchor.target = '_blank';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);

    this._amplitudeService.trackEvent('meeting_video_downloaded', {
      opportunity_id: this.opportunityId,
      meeting_bot_id: this.botId
    });
  }

  onVideoPlay(): void {
    this.isVideoPlaying = true;
    this.isVideoLoading = false;
  }

  onVideoPause(): void {
    this.isVideoPlaying = false;
  }

  onVideoLoadStart(): void {
    this.isVideoLoading = true;
  }

  onVideoCanPlay(): void {
    this.isVideoLoading = false;
  }

  onVideoLoadedData(): void {
    this.isVideoLoading = false;
  }

  openEmailPopup(): void {
    this.emailModal.open();
  }

  hasUnsavedChanges(): boolean {
    return Object.keys(this.editValues).length > 0;
  }

  get answeredQuestions(): OpportunityQuestion[] {
    return this.opportunityCardDetails.filter(q =>
      q.answers?.[0]?.answer_text?.trim()
    );
  }

  get unansweredQuestions(): OpportunityQuestion[] {
    return this.opportunityCardDetails.filter(q =>
      !q.answers?.[0]?.answer_text?.trim()
    );
  }

  toggleUnanswered(): void {
    this.isUnansweredCollapsed = !this.isUnansweredCollapsed;
    this.setScrollHeight();
  }

  onSummaryOptionClick(mode: 'short' | 'extended', event: MouseEvent): void {
    event.stopPropagation();
    this.setSummaryMode(mode);
  }

  onSummaryToggleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.toggleSummaryMode();
  }

  get activeSummaryDetail(): string {
    return this.summaryVariants[this.summaryMode] ?? '';
  }

  private loadOpportunityDates(opportunityId: string): void {
    this.isLoading = true;
    this._opportunityCardDetailService.getOpportunityDates(opportunityId).subscribe({
      next: (data: OpportunityDate[]) => {
        const sorted = data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        if (!sorted.length) {
          this.opportunityDates = [];
          this.lastUpdated = '';
          this.botId = '';
          this.clearMeetingData();
          this.isLoading = false;
          return;
        }

        this.opportunityDates = sorted;
        const targetBotId = this.botId || sorted[0]?.meeting_bot_id || '';
        const targetDate = sorted.find(d => d.meeting_bot_id === targetBotId)?.created_at ?? sorted[0]?.created_at ?? '';
        this.botId = targetBotId;
        this.lastUpdated = targetDate;
        this.loadAllMeetingData(targetBotId, targetDate);
        
        // Let child loading indicators take over for the actual data
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  private initializeCrmSearchStream(): void {
    this._crmSearchSubscription = this._crmSearchInput$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((term) => {
          const normalized = term.trim();
          if (!this.connectedCrmProvider) {
            this.crmSearchResults = [];
            this.crmSearchStatusMessage = 'Connect a CRM provider to map opportunities.';
            return of<CrmRecordSearchItem[]>([]);
          }

          if (normalized.length < 3) {
            this.crmSearchResults = [];
            this.crmSearchStatusMessage = normalized.length
              ? 'Enter at least 3 characters to search records.'
              : '';
            return of<CrmRecordSearchItem[]>([]);
          }

          this.isCrmSearchLoading = true;
          this.crmSearchStatusMessage = '';
          return from(this._opportunityCardDetailService.searchCrmRecords(this.connectedCrmProvider, normalized)).pipe(
            catchError((error) => {
              console.error('Failed to search CRM records:', error);
              this.crmSearchStatusMessage = 'Unable to fetch records right now.';
              return of<CrmRecordSearchItem[]>([]);
            })
          );
        })
      )
      .subscribe((results) => {
        this.crmSearchResults = results;
        if (!this.crmSearchStatusMessage && this.crmSearchTerm.trim().length >= 3 && !results.length) {
          this.crmSearchStatusMessage = 'No records found for this search.';
        }
        this.isCrmSearchLoading = false;
      });
  }

  private async loadCrmMappingContext(): Promise<void> {
    this.connectedCrmProvider = await this._userConfigService.loadConnectedCrmProvider();
    this.crmSearchTerm = '';
    this.crmSearchResults = [];
    this.crmSearchStatusMessage = '';
    this.selectedCrmRecord = null;
    this.currentCrmMapping = null;

    if (!this.connectedCrmProvider || !this.opportunityId) {
      return;
    }

    this._opportunityCardDetailService
      .getCurrentCrmMapping(this.opportunityId, this.connectedCrmProvider)
      .pipe(take(1))
      .subscribe({
        next: (mapping) => {
          this.currentCrmMapping = mapping;
          if (mapping?.crm_record_id) {
            this.crmSearchTerm = mapping.crm_object_name || mapping.crm_record_id;
            console.log('Existing CRM mapping found:', mapping);
          }
        },
        error: (error) => {
          console.error('Failed to load CRM mapping context:', error);
        },
      });
  }

  private clearMeetingData(): void {
    this.opportunityCardDetails = [];
    this.meetingTranscripts = [];
    this.nextStepGroups = [];
    this.recordingUrl = '';
    this.summaryVariants = {
      short: 'No summary available yet.',
      extended: ''
    };
    this.isExtendedSummaryAvailable = false;
  }

  private loadAllMeetingData(botId: string, createdAt: string): void {
    this.getOpportunityDetail(this.opportunityId, createdAt);
    this.loadPostCallData(botId);
    this.loadMeetingTranscript(botId);
    this.loadMeetingRecording(botId);
  }

  private getOpportunityDetail(opportunityId: string, createdAt: string): void {
    this._opportunityCardDetailService
      .getOpportunityDetail(opportunityId, createdAt)
      .subscribe((data: OpportunityDetail) => {
        this.opportunityCardDetails = data.questions.filter(q =>
          q.question_status || !!q.answers || !!q.previous_answer_summary
        );
      });
  }

  /**
   * Summary, extended summary and next steps all live on the same
   * post_call_processed row, so they're fetched together in one request.
   */
  private loadPostCallData(meetingBotId: string): void {
    this._opportunityCardDetailService.getPostCallProcessed(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        const row = res?.[0] ?? {};

        const rawSummary = row.summary ?? '';
        const formattedShort = this.formatSummary(rawSummary);
        const highlightedShort = formattedShort || 'No summary available yet.';

        const rawExtended = row.extended_summary ?? '';
        const hasExtendedSummary = !!rawExtended?.trim();
        const formattedExtended = hasExtendedSummary ? this.formatSummary(rawExtended) : '';

        this.summaryVariants = {
          short: highlightedShort,
          extended: hasExtendedSummary && formattedExtended
            ? formattedExtended
            : this.extendedSummaryUnavailableCopy
        };

        this.isExtendedSummaryAvailable = hasExtendedSummary;
        this.summaryMode = 'short';

        this.nextStepGroups = this.formatNextSteps(row.next_steps ?? '');

        this.setScrollHeight();
      },
      error: () => {
        this.summaryVariants = {
          short: 'No summary available yet.',
          extended: this.extendedSummaryUnavailableCopy
        };
        this.isExtendedSummaryAvailable = false;
        this.summaryMode = 'short';
        this.nextStepGroups = [];
        this.setScrollHeight();
      }
    });
  }

  /**
   * Parse the markdown-ish next_steps string into headed groups of bullets.
   * `**Heading**` starts a new bold section; `* item` lines become bullets.
   * Inline `**bold**` inside an item is preserved as <strong>.
   */
  private formatNextSteps(raw: string): NextStepGroup[] {
    const text = (raw ?? '').replace(/\r/g, '').trim();
    if (!text) return [];

    const groups: NextStepGroup[] = [];
    let current: NextStepGroup | null = null;

    const ensureGroup = (): NextStepGroup => {
      if (!current) {
        current = { heading: null, items: [] };
        groups.push(current);
      }
      return current;
    };

    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

    for (const line of lines) {
      const headingMatch = line.match(/^\*\*(.+?)\*\*:?$/);
      if (headingMatch) {
        current = { heading: headingMatch[1].trim(), items: [] };
        groups.push(current);
        continue;
      }

      // Split a line into bullets on "* " markers (but not on "**" bold).
      const parts = line.split(/\s*\*\s+/).map(p => p.trim()).filter(p => p.length > 0);
      const items = parts.length ? parts : [line];
      items.forEach(item => ensureGroup().items.push(this.boldify(item)));
    }

    return groups.filter(g => g.heading || g.items.length);
  }

  private boldify(text: string): string {
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  }

  private loadMeetingTranscript(meetingBotId: string): void {
    this._opportunityCardDetailService.getTranscript(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        this.meetingTranscripts = res;
      }
    });
  }

  private loadMeetingRecording(meetingBotId: string): void {
    this.isVideoPlaying = false;
    this.isVideoLoading = false;

    if (!meetingBotId?.trim()) {
      this.recordingUrl = '';
      return;
    }

    this._opportunityCardDetailService.getMeetingRecording(meetingBotId).pipe(take(1)).subscribe({
      next: (res) => {
        const url = res?.recordings?.[0]?.media_shortcuts?.video_mixed?.data?.download_url;
        this.recordingUrl = url ?? '';
      },
      error: () => {
        this.recordingUrl = '';
      }
    });
  }

  private setScrollHeight(): void {
    requestAnimationFrame(() => {
      if (!this.leftCol?.nativeElement || !this.rightPane?.nativeElement) {
        return;
      }

      if (window.matchMedia('(max-width: 1080px)').matches) {
        this.rightPane.nativeElement.style.height = 'auto';
        return;
      }

      this.rightPane.nativeElement.style.height = `${this.leftCol.nativeElement.offsetHeight}px`;
    });
  }

  private observeLeftColumnHeight(): void {
    if (typeof ResizeObserver === 'undefined' || !this.leftCol?.nativeElement) {
      return;
    }

    this._leftColResizeObserver = new ResizeObserver(() => {
      this.setScrollHeight();
    });

    this._leftColResizeObserver.observe(this.leftCol.nativeElement);
  }

  private formatSummary(text: string): string {
    const trimmed = text?.trim();
    if (!trimmed) return '';

    let formatted = trimmed
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*/g, '<br/>• ')
      .replace(/<strong>([^<:]+):<\/strong>\s*<br\/>/g, '<strong>$1:</strong><br/>');

    const firstBreakIndex = formatted.indexOf('<br/>');
    if (firstBreakIndex !== -1) {
      formatted =
        formatted.slice(0, firstBreakIndex) +
        formatted.slice(firstBreakIndex + '<br/>'.length);
    }

    return formatted;
  }

  private setSummaryMode(mode: 'short' | 'extended'): void {
    if (mode === 'extended' && !this.isExtendedSummaryAvailable) return;
    if (this.summaryMode === mode) return;
    this.summaryMode = mode;
    this.setScrollHeight();
  }

  private toggleSummaryMode(): void {
    const mode = this.summaryMode === 'short' ? 'extended' : 'short';
    this.setSummaryMode(mode);
  }
}